// Global Instructions Rule Applied!
import { useState, useEffect, useCallback } from 'react'
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../utils/endpoints.ts'
import { getAccessToken } from '../auth/authService'
import { store } from '../../state_manager/store.ts'

/**
 * One city typeahead hit, as CitySelect consumes it.
 * @typedef {{ label: string, value: string, name: string, countryCode: string, state: string | null | undefined, country: string | null | undefined }} CityOption
 */
/** @type {CityOption[]} */
const NO_CITIES = []

// Backend API configuration
const BACKEND_BASE_URL = getBackendBaseUrl()

/**
 * Custom hook for city typeahead search with debouncing
 *
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 300)
 * @returns the hook API — searchCities, loading, error, results, clearResults, hasSearched (shape inferred from the returned literal)
 */
export const useCitySearch = (debounceMs = 500) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [results, setResults] = useState(NO_CITIES)
  const [searchTerm, setSearchTerm] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const [currentCountry, setCurrentCountry] = useState(null)

  // API search function - doesn't set state terms, just performs the search
  const performCitySearch = useCallback(async (query, country = null) => {
    const trimmedQuery = query?.trim() || ''
    const tmpResults = []

    // Clear results if query is empty or too short
    if (!trimmedQuery || trimmedQuery.length < 2) {
      setResults([])
      setLoading(false)
      setError(null)
      setHasSearched(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const currentToken = getAccessToken(store.getState().auth)
      const headers = {
        'Content-Type': 'application/json'
      }

      if (currentToken) {
        headers['Authorization'] = `Bearer ${currentToken}`
      }

      const searchParams = new URLSearchParams({
        search: trimmedQuery,
        limit: '20'
      })

      // Add country filter if provided
      if (country) {
        searchParams.append('country', country)
      }

      const response = await fetch(
        buildFullUrl(
          BACKEND_BASE_URL,
          API_ROUTES.CITIES.SEARCH,
          {},
          {
            search: trimmedQuery,
            limit: '20',
            ...(country && { country })
          }
        ),
        {
          method: API_ROUTES.CITIES.SEARCH.method,
          headers
        }
      )

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed - please log in again')
        }
        throw new Error(`Failed to search cities: ${response.status} ${response.statusText}`)
      }

      const result = await response.json()

      // Process results
      result.data.forEach((cityResult) => {
        const locationPart = cityResult.state
          ? `${cityResult.state} - ${cityResult.countryCode}`
          : cityResult.countryCode
        tmpResults.push({
          label: `${cityResult.name} (${locationPart})`,
          value: cityResult.value,
          name: cityResult.name,
          countryCode: cityResult.countryCode,
          state: cityResult.state,
          country: cityResult.country
        })
      })

      setResults(tmpResults)
      setHasSearched(true)
    } catch (error) {
      console.error('Error searching cities:', error)
      setError(error.message || 'An unexpected error occurred while searching cities')
      setResults([])
      setHasSearched(true)
    } finally {
      setLoading(false)
    }
  }, [])

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm && searchTerm.length >= 2) {
        performCitySearch(searchTerm, currentCountry)
      } else if (!searchTerm) {
        // Clear results when search term is empty
        setResults([])
        setError(null)
        setLoading(false)
        setHasSearched(false)
      }
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [searchTerm, currentCountry, performCitySearch, debounceMs])

  // Clear results function
  const clearResults = useCallback(() => {
    setResults([])
    setSearchTerm('')
    setCurrentCountry(null)
    setError(null)
    setLoading(false)
    setHasSearched(false)
  }, [])

  // Debounced search trigger - only sets state, let useEffect handle the API call
  const triggerSearch = useCallback(
    /** @param {string} query @param {string | null} [country] */ (query, country = null) => {
    setSearchTerm(query?.trim() || '')
    setCurrentCountry(country)
    // Don't call searchCities directly - let the useEffect handle it with debounce
  }, [])

  return {
    searchCities: triggerSearch,
    loading,
    error,
    results,
    clearResults,
    hasSearched
  }
}
